import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiUrl } from '../apiConfig';

const Reconciliation = () => {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [reconciliationData, setReconciliationData] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '', visible: false });
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeTab, setActiveTabRaw] = useState('orphaned_products');
  const setActiveTab = (tab) => {
    setActiveTabRaw(tab);
    setSelectedItems([]);
  };
  const [validatingItem, setValidatingItem] = useState(null);
  const [clearingIds, setClearingIds] = useState(false);
  const [deletingItems, setDeletingItems] = useState(false);

  const showMessage = (text, type) => {
    setMessage({ text, type, visible: true });
    setTimeout(() => {
      setMessage(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  const runReconciliation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(apiUrl('/api/reconciliation/check/'), {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        showMessage('Session expired. Please log in again.', 'error');
        checkAuth();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to run reconciliation check');
      }

      const data = await response.json();
      setReconciliationData(data);
      setSelectedItems([]);
      showMessage('Reconciliation check completed', 'success');
    } catch (err) {
      setError(err.message);
      showMessage('Failed to run reconciliation check', 'error');
    } finally {
      setLoading(false);
    }
  }, [checkAuth]);

  const validateSingleItem = async (itemId) => {
    try {
      setValidatingItem(itemId);
      
      const response = await fetch(apiUrl('/api/reconciliation/validate-single/'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ item_id: itemId }),
      });

      if (!response.ok) {
        throw new Error('Validation failed');
      }

      const data = await response.json();
      
      if (data.is_valid) {
        showMessage(`Item "${data.name}" is valid in Shopify`, 'success');
      } else {
        showMessage(`Item "${data.name}" has issues: ${data.issues.join(', ')}`, 'error');
      }
    } catch (err) {
      showMessage('Failed to validate item', 'error');
    } finally {
      setValidatingItem(null);
    }
  };

  const clearShopifyIds = async () => {
    if (selectedItems.length === 0) {
      showMessage('Please select items to clear', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to clear Shopify IDs from ${selectedItems.length} item(s)? This will allow them to be re-uploaded to Shopify.`)) {
      return;
    }

    try {
      setClearingIds(true);
      
      const response = await fetch(apiUrl('/api/reconciliation/clear-shopify-ids/'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ item_ids: selectedItems }),
      });

      if (!response.ok) {
        throw new Error('Failed to clear Shopify IDs');
      }

      const data = await response.json();
      showMessage(data.message, 'success');
      setSelectedItems([]);
      
      // Re-run reconciliation to update the list
      runReconciliation();
    } catch (err) {
      showMessage('Failed to clear Shopify IDs', 'error');
    } finally {
      setClearingIds(false);
    }
  };

  const deleteSelectedItems = async () => {
    if (selectedItems.length === 0) {
      showMessage('Please select items to delete', 'error');
      return;
    }

    if (!window.confirm(`Are you sure you want to permanently delete ${selectedItems.length} item(s) from the database? This cannot be undone.`)) {
      return;
    }

    try {
      setDeletingItems(true);
      
      const response = await fetch(apiUrl('/api/reconciliation/delete-items/'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ item_ids: selectedItems }),
      });

      if (response.status === 401) {
        showMessage('Session expired. Please log in again.', 'error');
        checkAuth();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to delete items');
      }

      const data = await response.json();
      showMessage(data.message, 'success');
      setSelectedItems([]);
      runReconciliation();
    } catch (err) {
      showMessage('Failed to delete items from database', 'error');
    } finally {
      setDeletingItems(false);
    }
  };

  const toggleSelectItem = (itemId) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const selectAllInTab = () => {
    const currentIssues = getCurrentTabIssues();
    const allIds = currentIssues.map(item => item.id);
    
    if (selectedItems.length === allIds.length && allIds.every(id => selectedItems.includes(id))) {
      setSelectedItems([]);
    } else {
      setSelectedItems(allIds);
    }
  };

  const getCurrentTabIssues = () => {
    if (!reconciliationData?.issues) return [];
    
    switch (activeTab) {
      case 'orphaned_products':
        return reconciliationData.issues.orphaned_products || [];
      case 'orphaned_variants':
        return reconciliationData.issues.orphaned_variants || [];
      case 'missing_variant_ids':
        return reconciliationData.issues.missing_variant_ids || [];
      case 'not_on_shopify':
        return reconciliationData.issues.not_on_shopify || [];
      default:
        return [];
    }
  };

  useEffect(() => {
    if (user) {
      runReconciliation();
    }
  }, [user, runReconciliation]);

  const IssueCard = ({ item, isSelected, onSelect, onValidate, isValidating }) => (
    <div className={`bg-white rounded-lg border ${isSelected ? 'border-[#2C2C2C] ring-2 ring-[#2C2C2C]' : 'border-[#E8E6E1]'} p-4 transition-all`}>
      <div className="flex items-start space-x-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(item.id)}
          className="mt-1 h-4 w-4 text-[#2C2C2C] rounded border-[#E8E6E1] focus:ring-[#2C2C2C]"
        />
        
        <div className="flex-shrink-0">
          {item.image_url ? (
            <img 
              src={item.image_url} 
              alt={item.name}
              className="w-16 h-16 object-cover rounded-lg"
            />
          ) : (
            <div className="w-16 h-16 bg-[#F5F4F2] rounded-lg flex items-center justify-center">
              <svg className="w-8 h-8 text-[#999]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-[#2C2C2C] truncate">{item.name}</h3>
          {item.variant_name && (
            <p className="text-xs text-[#666] mt-0.5">Variant: {item.variant_name}</p>
          )}
          <p className="text-xs text-[#999] mt-1">ID: {item.id}</p>
          
          <div className="mt-2 space-y-1">
            <p className="text-xs text-[#666]">
              <span className="font-medium">Product ID:</span>{' '}
              <span className="font-mono text-[10px]">{item.shopify_product_id || 'None'}</span>
            </p>
            <p className="text-xs text-[#666]">
              <span className="font-medium">Variant ID:</span>{' '}
              <span className="font-mono text-[10px]">{item.shopify_variant_id || 'None'}</span>
            </p>
          </div>
          
          <div className="mt-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {item.reason}
            </span>
          </div>
        </div>
        
        <div className="flex-shrink-0">
          <button
            onClick={() => onValidate(item.id)}
            disabled={isValidating}
            className="px-3 py-1.5 text-xs font-medium text-[#2C2C2C] bg-[#F5F4F2] rounded-lg hover:bg-[#E8E6E1] transition-colors disabled:opacity-50"
          >
            {isValidating ? 'Checking...' : 'Re-validate'}
          </button>
        </div>
      </div>
    </div>
  );

  const TabButton = ({ id, label, count, isActive, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
        isActive 
          ? 'bg-[#2C2C2C] text-white' 
          : 'text-[#666] hover:bg-[#F5F4F2]'
      }`}
    >
      {label}
      {count > 0 && (
        <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
          isActive ? 'bg-white/20 text-white' : 'bg-red-100 text-red-800'
        }`}>
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#2C2C2C]">Shopify Reconciliation</h1>
        <p className="text-[#666] mt-1">
          Check for mismatches between your app database and Shopify store
        </p>
      </div>

      {/* Message Toast */}
      {message.visible && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {message.text}
        </div>
      )}

      {/* Action Bar */}
      <div className="bg-white rounded-xl border border-[#E8E6E1] p-4 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={runReconciliation}
              disabled={loading}
              className="px-4 py-2 bg-[#2C2C2C] text-white rounded-lg font-medium hover:bg-[#1a1a1a] transition-colors disabled:opacity-50 flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Checking...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Run Reconciliation</span>
                </>
              )}
            </button>
            
            {selectedItems.length > 0 && (
              <>
                <button
                  onClick={deleteSelectedItems}
                  disabled={deletingItems}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {deletingItems ? (
                    <span>Deleting...</span>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Delete from Database ({selectedItems.length})</span>
                    </>
                  )}
                </button>
                <button
                  onClick={clearShopifyIds}
                  disabled={clearingIds}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 flex items-center space-x-2"
                >
                  {clearingIds ? (
                    <span>Clearing...</span>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Clear Shopify IDs ({selectedItems.length})</span>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
          
          {reconciliationData?.summary && (
            <div className="text-sm text-[#666]">
              Last checked: {new Date().toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {reconciliationData?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-[#E8E6E1] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#666]">Total Items Checked</p>
                <p className="text-2xl font-bold text-[#2C2C2C]">{reconciliationData.summary.total_items_checked}</p>
              </div>
              <div className="w-12 h-12 bg-[#F5F4F2] rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-[#2C2C2C]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Valid Items</p>
                <p className="text-2xl font-bold text-green-700">{reconciliationData.summary.valid_items}</p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600">Total Issues</p>
                <p className="text-2xl font-bold text-red-700">
                  {reconciliationData.summary.orphaned_products + 
                   reconciliationData.summary.orphaned_variants + 
                   reconciliationData.summary.missing_variant_ids}
                </p>
              </div>
              <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-yellow-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600">Not on Shopify</p>
                <p className="text-2xl font-bold text-yellow-700">{reconciliationData.summary.items_without_shopify_id}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-blue-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Shopify Products</p>
                <p className="text-2xl font-bold text-blue-700">{reconciliationData.summary.shopify_products_count}</p>
                <p className="text-xs text-blue-500">{reconciliationData.summary.shopify_variants_count} variants</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issues Section */}
      {reconciliationData?.issues && (
        <div className="bg-white rounded-xl border border-[#E8E6E1] overflow-hidden">
          {/* Tabs */}
          <div className="border-b border-[#E8E6E1] p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex space-x-2">
                <TabButton
                  id="orphaned_products"
                  label="Orphaned Products"
                  count={reconciliationData.issues.orphaned_products?.length || 0}
                  isActive={activeTab === 'orphaned_products'}
                  onClick={setActiveTab}
                />
                <TabButton
                  id="orphaned_variants"
                  label="Orphaned Variants"
                  count={reconciliationData.issues.orphaned_variants?.length || 0}
                  isActive={activeTab === 'orphaned_variants'}
                  onClick={setActiveTab}
                />
                <TabButton
                  id="missing_variant_ids"
                  label="Missing Variant IDs"
                  count={reconciliationData.issues.missing_variant_ids?.length || 0}
                  isActive={activeTab === 'missing_variant_ids'}
                  onClick={setActiveTab}
                />
                <TabButton
                  id="not_on_shopify"
                  label="Not on Shopify"
                  count={reconciliationData.issues.not_on_shopify?.length || 0}
                  isActive={activeTab === 'not_on_shopify'}
                  onClick={setActiveTab}
                />
              </div>
              
              {getCurrentTabIssues().length > 0 && (
                <button
                  onClick={selectAllInTab}
                  className="px-3 py-1.5 text-sm text-[#666] hover:text-[#2C2C2C] transition-colors"
                >
                  {selectedItems.length === getCurrentTabIssues().length ? 'Deselect All' : 'Select All'}
                </button>
              )}
            </div>
          </div>
          
          {/* Tab Content */}
          <div className="p-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <svg className="animate-spin h-8 w-8 text-[#2C2C2C]" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : getCurrentTabIssues().length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto text-green-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-[#666]">No issues found in this category</p>
              </div>
            ) : (
              <div className="space-y-4">
                {getCurrentTabIssues().map(item => (
                  <IssueCard
                    key={item.id}
                    item={item}
                    isSelected={selectedItems.includes(item.id)}
                    onSelect={toggleSelectItem}
                    onValidate={validateSingleItem}
                    isValidating={validatingItem === item.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <svg className="w-12 h-12 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-red-700 font-medium">{error}</p>
          <button
            onClick={runReconciliation}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Help Section */}
      <div className="mt-6 bg-[#F5F4F2] rounded-xl p-6">
        <h3 className="text-lg font-semibold text-[#2C2C2C] mb-3">Understanding the Issues</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-medium text-[#2C2C2C] mb-2 flex items-center">
              <span className="w-3 h-3 rounded-full bg-red-500 mr-2"></span>
              Orphaned Products
            </h4>
            <p className="text-sm text-[#666]">
              Products that exist in your app but have been deleted from Shopify. 
              The stored Product ID no longer exists.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-medium text-[#2C2C2C] mb-2 flex items-center">
              <span className="w-3 h-3 rounded-full bg-orange-500 mr-2"></span>
              Orphaned Variants
            </h4>
            <p className="text-sm text-[#666]">
              Products where the variant ID is invalid. The product exists but 
              the specific variant has been deleted or changed.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-medium text-[#2C2C2C] mb-2 flex items-center">
              <span className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></span>
              Missing Variant IDs
            </h4>
            <p className="text-sm text-[#666]">
              Products that have a Shopify Product ID but no Variant ID stored. 
              May need to be re-synced.
            </p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="font-medium text-[#2C2C2C] mb-2 flex items-center">
              <span className="w-3 h-3 rounded-full bg-yellow-600 mr-2"></span>
              Not on Shopify
            </h4>
            <p className="text-sm text-[#666]">
              Items in the database with no Shopify IDs at all. Never uploaded 
              or previously cleared. Can be deleted or re-uploaded.
            </p>
          </div>
        </div>
        
        <div className="mt-4 p-4 bg-white rounded-lg border border-[#E8E6E1]">
          <h4 className="font-medium text-[#2C2C2C] mb-2">How to Fix Issues</h4>
          <ol className="text-sm text-[#666] space-y-2 list-decimal list-inside">
            <li>Select the items with issues using the checkboxes</li>
            <li>Click "Clear Shopify IDs" to remove the invalid references</li>
            <li>Go to the Upload page and re-upload the items to Shopify</li>
            <li>Run reconciliation again to verify the fix</li>
          </ol>
        </div>
      </div>
    </div>
  );
};

export default Reconciliation;
